/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, {useEffect, useMemo, useState} from 'react'
import {Combobox, ComboboxButton, ComboboxInput, ComboboxLabel, ComboboxOptions} from '@headlessui/react'
import {ChevronDownIcon, PlusIcon} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import {useTranslation} from 'react-i18next'
import {useTypedSelector} from 'helpers/reducers'
import {useAppDispatch} from 'helpers/store'
import LeadsAction from 'store/leads/LeadsAction'
import {addLeadTagsToCatalog} from 'store/leads/LeadsReducer'
import {MULTISELECT_DEBOUNCE_MS, useDebouncedCallback} from 'utils/hooks/useDebounce'
import LeadTagsSelectedList from './LeadTagsAutocompleteComponents/LeadTagsSelectedList'
import LeadTagsOptionsList from './LeadTagsAutocompleteComponents/LeadTagsOptionsList'
import {
    uiFormControlFocusClassName,
    uiFormControlPlaceholderClassName,
    uiFormControlSurfaceClassName,
} from 'components/Ui/UiFormControlStyles'

interface LeadTagsAutocompleteProps {
    selectedTags: string[]
    onTagsChange: (tags: string[]) => void
    label?: string
    placeholder?: string
    /** When set, only these tags are suggested (e.g. remove-tags from selected rows). */
    availableTags?: string[]
    /** Allow creating a new tag by typing. Defaults to true. */
    allowCreate?: boolean
}

const LeadTagsAutocomplete: React.FC<LeadTagsAutocompleteProps> = ({
    selectedTags: rawSelectedTags,
    onTagsChange,
    label,
    placeholder,
    availableTags,
    allowCreate = true,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const leads = useTypedSelector((state) => state.leadsState.all)
    const tagsCatalog = useTypedSelector((state) => state.leadsState.tagsCatalog)
	const [hasLoadedTags, setHasLoadedTags] = useState<boolean>(false)

    const normalizedPropTags = useMemo(() => {
        return (rawSelectedTags || []).filter((tag) => typeof tag === 'string' && tag.trim() !== '')
    }, [rawSelectedTags])

    const [selectedTags, setSelectedTags] = useState<string[]>(normalizedPropTags)

    useEffect(() => {
        setSelectedTags(normalizedPropTags)
    }, [normalizedPropTags])

    const debouncedOnTagsChange = useDebouncedCallback((tags: string[]) => {
        onTagsChange(tags)
    }, MULTISELECT_DEBOUNCE_MS)

    const existingTags = useMemo(() => {
        const tags = new Set<string>()
        leads.forEach((lead) => {
            if (lead.tags) {
                lead.tags.forEach((tag) => {
                    if (tag && typeof tag === 'string' && tag.trim() !== '') {
                        tags.add(tag.trim())
                    }
                })
            }
        })
        return Array.from(tags).sort()
    }, [leads])

    const sourceTags = useMemo(() => {
        if (availableTags) {
            return Array.from(
                new Set(
                    availableTags
                        .filter((tag) => typeof tag === 'string' && tag.trim() !== '')
                        .map((tag) => tag.trim())
                )
            ).sort((a, b) => a.localeCompare(b))
        }
        return tagsCatalog && tagsCatalog.length > 0 ? tagsCatalog : existingTags
    }, [availableTags, existingTags, tagsCatalog])

    useEffect(() => {
        if (availableTags || hasLoadedTags) {
            return
        }
        dispatch(LeadsAction.fetchLeadTags()).then(() => {
            setHasLoadedTags(true)
        })
    }, [availableTags, dispatch, hasLoadedTags])

    const [query, setQuery] = useState('')

    const filteredTags =
        query === ''
            ? sourceTags
            : sourceTags.filter((tag) => tag.toLowerCase().includes(query.toLowerCase()))

    const canCreateFromQuery =
        allowCreate &&
        query.trim() !== '' &&
        !sourceTags.some((tag) => tag.toLowerCase() === query.trim().toLowerCase())

    const handleAddTag = (tag: string) => {
        const trimmedTag = tag.trim()
        if (!trimmedTag) {
            setQuery('')
            return
        }

        if (!allowCreate && !sourceTags.includes(trimmedTag)) {
            setQuery('')
            return
        }

        if (allowCreate) {
            dispatch(addLeadTagsToCatalog(trimmedTag))
        }
        if (!selectedTags.includes(trimmedTag)) {
            const next = [...selectedTags, trimmedTag]
            setSelectedTags(next)
            debouncedOnTagsChange(next)
        }
        setQuery('')
    }

    const removeTag = (tagToRemove: string) => {
        const next = selectedTags.filter((t) => t !== tagToRemove)
        setSelectedTags(next)
        debouncedOnTagsChange(next)
    }

    return (
        <div className="LeadTagsAutocomplete w-full">
            <Combobox value={query} onChange={(val: string | null) => { if (val) handleAddTag(val) }}>
                {label && (
                    <ComboboxLabel className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {label}
                    </ComboboxLabel>
                )}
                <div className="relative">
                    <div
                        className={clsx(
                            'relative flex items-center overflow-hidden',
                            uiFormControlSurfaceClassName,
                            uiFormControlFocusClassName
                        )}
                    >
                        <ComboboxInput
                            value={query}
                            className={clsx(
                                'min-w-0 flex-1 border-none bg-transparent py-2 pl-3 pr-2 text-sm text-gray-900 focus:ring-0 focus:outline-none dark:text-gray-100',
                                uiFormControlPlaceholderClassName
                            )}
                            placeholder={placeholder || t('Search or type tag...')}
                            onChange={(event) => setQuery(event.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && query) {
                                    e.preventDefault()
                                    handleAddTag(query)
                                }
                            }}
                        />
                        {canCreateFromQuery && (
                            <button
                                type="button"
                                onClick={() => handleAddTag(query)}
                                className="flex items-center gap-1 whitespace-nowrap px-2 py-1 rounded text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 shrink-0"
                            >
                                <PlusIcon className="h-3.5 w-3.5" />
                                {t('Add')}
                            </button>
                        )}
                        <ComboboxButton className="flex items-center pr-2">
                            <ChevronDownIcon className="h-5 w-5 text-gray-800 dark:text-gray-200" aria-hidden="true" />
                        </ComboboxButton>
                    </div>
                    {(filteredTags.length > 0 || canCreateFromQuery) && (
                    <ComboboxOptions
                        anchor="bottom start"
                        portal
                        modal={false}
                        className="z-9999 mt-1 max-h-60 w-(--input-width) overflow-auto overscroll-contain rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm dark:bg-gray-950 dark:ring-white/10"
                    >
                        <LeadTagsOptionsList
                            query={query}
                            existingTags={sourceTags}
                            filteredTags={filteredTags}
                            selectedTags={selectedTags}
                            translate={t}
                        />
                    </ComboboxOptions>
                    )}
                </div>
            </Combobox>
            {selectedTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    <LeadTagsSelectedList tags={selectedTags} onRemove={removeTag} />
                </div>
            )}
        </div>
    )
}

export default LeadTagsAutocomplete
